/**
 * useLiveScreenData.ts — Derived hook for the Live Screen.
 *
 * UI-INV-003: Derived hooks are the view-model layer.
 * Internally uses deriveLiveScreenData() pure function.
 */
import { useRetreat } from "./useRetreat";
import type { EngineSnapshot } from "@retreats/engine";
import type { Container, EditableRetreatSchedule } from "@retreat/core";
import { findWhisper } from "@retreats/content";
import { fmtClock } from "../utils/scheduleUtils";

export type LiveScreenData = {
  remaining: string;
  total: string;
  remainingFraction: number;
  retreatProgressFraction: number;
  remainingSeconds: number;
  elapsedSeconds: number;
  totalDurationSeconds: number;
  session: { name: string; kind: string };
  next: { name: string; atLabel: string } | null;
  /** Day-level progress for the multi-day label. count===1 means single-day — hide the label. */
  dayProgress: { idx: number; count: number };
  /** Index of the current container within the active day's containers. */
  containerIdx: number;
  mode: "solo" | "leader";
  retreatName: string;
  endsAtLabel: string;
  retreatStartLabel: string;
  retreatEndsLabel: string;
};

const TYPE_LABELS: Partial<Record<Container["type"], string>> = {
  meditation: "practice",
  meal: "meal",
  teaching: "teaching",
  work: "work",
  sleep: "rest",
  physical_practice: "movement",
  chant: "chanting",
};

function containerKind(c: Container): string {
  let typeLabel: string;
  if (c.type === "meditation") {
    typeLabel = c.posture === "walking" ? "walking practice" : "seated practice";
  } else {
    typeLabel = TYPE_LABELS[c.type] ?? c.type;
  }
  if (c.traditionTerm) return `${c.traditionTerm} · ${typeLabel}`;
  return typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1);
}

/**
 * Pure function — testable without React.
 */
export function deriveLiveScreenData(
  snapshot: EngineSnapshot,
  schedule: EditableRetreatSchedule,
): LiveScreenData {
  const elapsed = snapshot.elapsedSeconds;
  const containers = schedule.days[0]?.containers ?? [];
  const totalScheduleSeconds = containers.reduce((s, c) => s + c.durationSeconds, 0);

  let containerStart = 0;
  let currentIdx = containers.length - 1;
  for (let i = 0; i < containers.length; i++) {
    if (elapsed < containerStart + containers[i].durationSeconds) {
      currentIdx = i;
      break;
    }
    if (i < containers.length - 1) {
      containerStart += containers[i].durationSeconds;
    }
  }

  const current = containers[currentIdx];
  const containerDuration = current.durationSeconds;
  const elapsedInContainer = Math.max(0, elapsed - containerStart);
  const containerRemaining = Math.max(0, containerDuration - elapsedInContainer);
  const remainingFraction = containerDuration > 0 ? containerRemaining / containerDuration : 0;
  const retreatProgressFraction = totalScheduleSeconds > 0
    ? Math.min(1, elapsed / totalScheduleSeconds)
    : 0;

  const nowMs = Date.now();
  const retreatStartMs = nowMs - elapsed * 1000;
  const retreatEndMs = retreatStartMs + totalScheduleSeconds * 1000;
  const containerEndMs = nowMs + containerRemaining * 1000;

  const nextContainer = containers[currentIdx + 1] ?? null;

  return {
    remaining: formatTime(containerRemaining),
    total: formatTime(containerDuration),
    remainingFraction,
    retreatProgressFraction,
    remainingSeconds: containerRemaining,
    elapsedSeconds: elapsed,
    totalDurationSeconds: totalScheduleSeconds,
    session: { name: current.name, kind: containerKind(current) },
    next: nextContainer
      ? { name: nextContainer.name, atLabel: formatTime(containerRemaining) }
      : null,
    dayProgress: { idx: 0, count: schedule.days.length },
    containerIdx: currentIdx,
    mode: "solo",
    retreatName: schedule.name ?? "retreat",
    endsAtLabel: fmtClock(containerEndMs),
    retreatStartLabel: fmtClock(retreatStartMs),
    retreatEndsLabel: fmtClock(retreatEndMs),
  };
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function useLiveScreenData(
  schedule: EditableRetreatSchedule,
  tradition?: string,
  intent?: string,
): LiveScreenData & { midpointWhisper?: string } {
  const snapshot = useRetreat();
  const data = deriveLiveScreenData(snapshot, schedule);

  const currentContainer = schedule.days[0]?.containers[data.containerIdx];
  const isMidpoint =
    currentContainer !== undefined &&
    currentContainer.durationSeconds >= 600 &&
    data.remainingFraction >= 0.45 &&
    data.remainingFraction <= 0.55;

  const seed = `${data.containerIdx}-${(schedule as { id?: string }).id ?? "default"}`;
  const midpointWhisper = isMidpoint
    ? findWhisper('mid-session', { tradition, intent, seed })?.line
    : undefined;

  return { ...data, midpointWhisper };
}
